if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/*
 * Permanent product-page punctuation guard.
 * Custom Liquid blocks must never create standalone dash text before the title,
 * after Add to cart, or elsewhere in the product information column.
 */
(() => {
  if (window.__dtProductDashGuardInstalled) return;
  window.__dtProductDashGuardInstalled = true;

  const standaloneDash = /^\s*[-–—]\s*$/;
  const observedSections = new WeakSet();

  const productInfoNodes = (root) => {
    const nodes = [];
    if (root?.matches?.('section[id^="ProductInfo-"]')) nodes.push(root);
    if (root?.querySelectorAll) {
      nodes.push(...root.querySelectorAll('product-info[id^="MainProduct-"] section[id^="ProductInfo-"]'));
    }
    return nodes;
  };

  const removeStandaloneDashes = (root = document) => {
    for (const productInfo of productInfoNodes(root)) {
      for (const node of [...productInfo.childNodes]) {
        if (node.nodeType === Node.TEXT_NODE && standaloneDash.test(node.textContent || '')) node.remove();
      }
    }
  };

  const productSections = (root) => {
    const sections = [];
    if (root?.matches?.('product-info[id^="MainProduct-"]')) sections.push(root);
    if (root?.querySelectorAll) sections.push(...root.querySelectorAll('product-info[id^="MainProduct-"]'));
    return sections;
  };

  const install = (root = document) => {
    removeStandaloneDashes(root);

    for (const section of productSections(root)) {
      if (observedSections.has(section)) continue;
      observedSections.add(section);

      new MutationObserver(() => removeStandaloneDashes(section)).observe(section, {
        childList: true,
        subtree: true,
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => install(document), { once: true });
  } else {
    install(document);
  }

  document.addEventListener('shopify:section:load', (event) => install(event.target));
})();


/*
 * Permanent TCA condition display guard.
 * Legacy records with multiple C grades display only the lowest numeric grade.
 * This changes storefront presentation only; it does not alter variant metafields.
 */
(() => {
  if (window.__dtTcaConditionDisplayGuardInstalled) return;
  window.__dtTcaConditionDisplayGuardInstalled = true;

  const conditionPhrases = {
    'C-10': 'Mint',
    'C-9': 'Factory New',
    'C-8': 'Like New',
    'C-7': 'Excellent',
    'C-6': 'Very Good',
    'C-5': 'Good',
    'C-4': 'Fair',
    'C-3': 'Standard',
    'C-2': 'Restoration candidate',
    'C-1': 'Junk',
  };
  const observedProducts = new WeakSet();

  const normalizeConditionRows = (root = document) => {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('.dt-condition-section .dt-detail-list > div').forEach((row) => {
      const label = row.querySelector('dt');
      const value = row.querySelector('dd');
      if (!label || !value || label.textContent.trim().toLowerCase() !== 'tca condition') return;

      const codes = [...new Set(value.textContent.match(/\bC-(?:10|[1-9])\b/g) || [])];
      if (codes.length < 2) return;

      const lowest = codes.reduce((selected, code) => {
        const selectedNumber = Number(selected.split('-')[1]);
        const codeNumber = Number(code.split('-')[1]);
        return codeNumber < selectedNumber ? code : selected;
      });
      const phrase = conditionPhrases[lowest];
      value.textContent = phrase ? `${lowest} (${phrase})` : lowest;
    });
  };

  const productRoots = (root = document) => {
    const products = [];
    if (root?.matches?.('product-info[id^="MainProduct-"]')) products.push(root);
    if (root?.querySelectorAll) {
      products.push(...root.querySelectorAll('product-info[id^="MainProduct-"]'));
    }
    return products;
  };

  const install = (root = document) => {
    normalizeConditionRows(root);

    for (const product of productRoots(root)) {
      if (observedProducts.has(product)) continue;
      observedProducts.add(product);

      new MutationObserver(() => normalizeConditionRows(product)).observe(product, {
        childList: true,
        subtree: true,
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => install(document), { once: true });
  } else {
    install(document);
  }

  document.addEventListener('shopify:section:load', (event) => install(event.target));
})();
